import React, { useState, useEffect } from 'react';

const WinterSceneOverlay = () => {
  const [snowflakes, setSnowflakes] = useState([]);

  useEffect(() => {
    // Generate more realistic snowflakes with depth
    const generateSnowflakes = () => {
      const flakes = [];
      for (let i = 0; i < 150; i++) {
        const depth = Math.random(); // 0 = far, 1 = near
        flakes.push({
          id: i,
          left: Math.random() * 100,
          animationDuration: 8 + Math.random() * 15 + (depth * 10),
          animationDelay: Math.random() * 5,
          size: 1 + depth * 5,
          opacity: 0.2 + depth * 0.6,
          sway: (Math.random() * 40 - 20) * (1 + depth),
          blur: (1 - depth) * 2,
        });
      }
      setSnowflakes(flakes);
    };

    generateSnowflakes();
  }, []);

  return (
    <div className="winter-scene-overlay">
      {/* Sky with gradient */}
      <div className="night-sky"></div>

      {/* Moon with realistic glow */}
      <div className="moon-container">
        <div className="moon-halo"></div>
        <div className="moon-glow"></div>
        <div className="moon-body"></div>
      </div>

      {/* Stars with depth */}
      <div className="stars-layer">
        {[...Array(80)].map((_, i) => {
          const depth = Math.random();
          return (
            <div
              key={i}
              className="star"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 70}%`,
                width: `${1 + depth * 2}px`,
                height: `${1 + depth * 2}px`,
                opacity: 0.3 + depth * 0.7,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${2 + Math.random() * 3}s`,
              }}
            />
          );
        })}
      </div>

      {/* Distant forest silhouette */}
      <div className="distant-forest">
        {[...Array(40)].map((_, i) => (
          <div
            key={i}
            className="distant-tree"
            style={{
              left: `${i * 2.5}%`,
              height: `${80 + Math.random() * 60}px`,
              animationDelay: `${Math.random() * 2}s`,
            }}
          />
        ))}
      </div>

      {/* Mid-distance pine trees */}
      <div className="mid-forest">
        {[...Array(25)].map((_, i) => (
          <div
            key={i}
            className="mid-tree"
            style={{
              left: `${i * 4}%`,
              height: `${120 + Math.random() * 80}px`,
              width: `${40 + Math.random() * 30}px`,
            }}
          />
        ))}
      </div>

      {/* Village with multiple cabins */}
      <div className="village-container">
        {/* Cabin 1 - Far left */}
        <div className="cabin cabin-1">
          <div className="cabin-roof">
            <div className="roof-snow"></div>
          </div>
          <div className="cabin-walls">
            <div className="wood-texture"></div>
            <div className="window warm-glow">
              <div className="window-frame"></div>
              <div className="window-light"></div>
            </div>
          </div>
          <div className="chimney">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="smoke-puff" style={{ animationDelay: `${i * 0.8}s` }} />
            ))}
          </div>
        </div>

        {/* Cabin 2 - Center left */}
        <div className="cabin cabin-2">
          <div className="cabin-roof">
            <div className="roof-snow"></div>
          </div>
          <div className="cabin-walls">
            <div className="wood-texture"></div>
            <div className="window warm-glow">
              <div className="window-frame"></div>
              <div className="window-light"></div>
            </div>
            <div className="cabin-door"></div>
            <div className="window warm-glow window-right">
              <div className="window-frame"></div>
              <div className="window-light"></div>
            </div>
          </div>
          <div className="chimney chimney-center">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="smoke-puff" style={{ animationDelay: `${i * 0.8 + 0.3}s` }} />
            ))}
          </div>
        </div>

        {/* Cabin 3 - Center */}
        <div className="cabin cabin-3">
          <div className="cabin-roof cabin-roof-large">
            <div className="roof-snow"></div>
          </div>
          <div className="cabin-walls cabin-walls-large">
            <div className="wood-texture"></div>
            <div className="window warm-glow">
              <div className="window-frame"></div>
              <div className="window-light"></div>
            </div>
            <div className="window warm-glow window-second">
              <div className="window-frame"></div>
              <div className="window-light"></div>
            </div>
          </div>
          <div className="chimney">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="smoke-puff" style={{ animationDelay: `${i * 0.8 + 0.6}s` }} />
            ))}
          </div>
        </div>

        {/* Cabin 4 - Center right */}
        <div className="cabin cabin-4">
          <div className="cabin-roof">
            <div className="roof-snow"></div>
          </div>
          <div className="cabin-walls">
            <div className="wood-texture"></div>
            <div className="window warm-glow">
              <div className="window-frame"></div>
              <div className="window-light"></div>
            </div>
          </div>
          <div className="chimney chimney-right">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="smoke-puff" style={{ animationDelay: `${i * 0.8 + 0.9}s` }} />
            ))}
          </div>
        </div>

        {/* Cabin 5 - Far right */}
        <div className="cabin cabin-5">
          <div className="cabin-roof">
            <div className="roof-snow"></div>
          </div>
          <div className="cabin-walls">
            <div className="wood-texture"></div>
            <div className="window warm-glow">
              <div className="window-frame"></div>
              <div className="window-light"></div>
            </div>
            <div className="window warm-glow window-right">
              <div className="window-frame"></div>
              <div className="window-light"></div>
            </div>
          </div>
          <div className="chimney">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="smoke-puff" style={{ animationDelay: `${i * 0.8 + 1.2}s` }} />
            ))}
          </div>
        </div>
      </div>

      {/* Foreground pine trees */}
      <div className="foreground-trees">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="foreground-tree"
            style={{
              left: `${i * 8.5}%`,
              height: `${180 + Math.random() * 120}px`,
              width: `${50 + Math.random() * 40}px`,
            }}
          />
        ))}
      </div>

      {/* Snowy ground with path */}
      <div className="snow-ground-layer">
        <div className="ground-texture"></div>
        <div className="snow-path"></div>
        <div className="snow-drifts"></div>
      </div>

      {/* Animated falling snow with depth layers */}
      <div className="falling-snow-container">
        {snowflakes.map((flake) => (
          <div
            key={flake.id}
            className="realistic-snowflake"
            style={{
              left: `${flake.left}%`,
              animationDuration: `${flake.animationDuration}s`,
              animationDelay: `${flake.animationDelay}s`,
              width: `${flake.size}px`,
              height: `${flake.size}px`,
              opacity: flake.opacity,
              filter: `blur(${flake.blur}px)`,
              '--sway': `${flake.sway}px`,
            }}
          />
        ))}
      </div>

      <style jsx>{`
        .winter-scene-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
          pointer-events: none;
          overflow: hidden;
        }

        .night-sky {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            to bottom,
            #0d1b2a 0%,
            #1b263b 20%,
            rgba(45, 64, 89, 0.9) 50%,
            rgba(65, 90, 119, 0.8) 75%,
            rgba(90, 122, 157, 0.7) 100%
          );
          z-index: 0;
        }

        .moon-container {
          position: absolute;
          top: 8%;
          right: 15%;
          width: 120px;
          height: 120px;
          z-index: 2;
        }

        .moon-halo {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 280px;
          height: 280px;
          background: radial-gradient(
            circle,
            rgba(255, 248, 220, 0.15) 0%,
            rgba(255, 248, 220, 0.08) 30%,
            rgba(200, 220, 255, 0.03) 60%,
            transparent 100%
          );
          animation: moonHaloGlow 10s ease-in-out infinite;
        }

        .moon-glow {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 200px;
          height: 200px;
          background: radial-gradient(
            circle,
            rgba(255, 248, 220, 0.4) 0%,
            rgba(255, 248, 220, 0.2) 40%,
            transparent 70%
          );
          filter: blur(20px);
          animation: moonGlow 8s ease-in-out infinite;
        }

        .moon-body {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 100%;
          height: 100%;
          background: radial-gradient(
            ellipse at 40% 35%,
            #fffef0 0%,
            #fef9e7 30%,
            #f4e8c1 60%,
            #e8d8a8 100%
          );
          border-radius: 50%;
          box-shadow: 
            inset -15px -15px 30px rgba(0, 0, 0, 0.15),
            0 0 60px rgba(255, 248, 220, 0.5),
            0 0 100px rgba(255, 248, 220, 0.3);
        }

        .moon-body::before {
          content: '';
          position: absolute;
          top: 25%;
          left: 55%;
          width: 15px;
          height: 15px;
          background: rgba(0, 0, 0, 0.08);
          border-radius: 50%;
          box-shadow: 
            -20px 15px 0 5px rgba(0, 0, 0, 0.05),
            10px -10px 0 3px rgba(0, 0, 0, 0.06);
        }

        .stars-layer {
          position: absolute;
          width: 100%;
          height: 70%;
          top: 0;
          z-index: 1;
        }

        .star {
          position: absolute;
          background: white;
          border-radius: 50%;
          animation: starTwinkle ease-in-out infinite;
          box-shadow: 0 0 4px rgba(255, 255, 255, 0.9);
        }

        .distant-forest {
          position: absolute;
          bottom: 40%;
          width: 100%;
          height: 200px;
          z-index: 2;
        }

        .distant-tree {
          position: absolute;
          bottom: 0;
          width: 0;
          height: 0;
          border-left: 15px solid transparent;
          border-right: 15px solid transparent;
          border-bottom: 100px solid rgba(20, 40, 60, 0.95);
          animation: treesSway ease-in-out infinite;
          filter: blur(2px);
        }

        .distant-tree::before {
          content: '';
          position: absolute;
          left: -12px;
          top: 25px;
          width: 0;
          height: 0;
          border-left: 12px solid transparent;
          border-right: 12px solid transparent;
          border-bottom: 70px solid rgba(20, 40, 60, 0.95);
        }

        .mid-forest {
          position: absolute;
          bottom: 30%;
          width: 100%;
          height: 250px;
          z-index: 3;
        }

        .mid-tree {
          position: absolute;
          bottom: 0;
          width: 40px;
          clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
          background: linear-gradient(
            to bottom,
            #1a3a3a 0%,
            #0f2828 100%
          );
          opacity: 0.98;
          filter: blur(1px);
        }

        .mid-tree::before {
          content: '';
          position: absolute;
          bottom: -10px;
          left: 50%;
          transform: translateX(-50%);
          width: 8px;
          height: 40px;
          background: rgba(30, 20, 10, 0.6);
        }

        .village-container {
          position: absolute;
          bottom: 22%;
          width: 100%;
          height: 200px;
          z-index: 4;
          opacity: 1;
        }

        .cabin {
          position: absolute;
          width: 100px;
          height: 90px;
        }

        .cabin-1 { left: 8%; transform: scale(0.85); }
        .cabin-2 { left: 22%; }
        .cabin-3 { left: 38%; transform: scale(1.15); }
        .cabin-4 { right: 26%; }
        .cabin-5 { right: 10%; transform: scale(0.9); }

        .cabin-roof {
          position: absolute;
          top: -35px;
          left: -12px;
          width: 0;
          height: 0;
          border-left: 62px solid transparent;
          border-right: 62px solid transparent;
          border-bottom: 50px solid #6b4423;
          filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.4));
        }

        .cabin-roof-large {
          left: -15px;
          border-left: 75px solid transparent;
          border-right: 75px solid transparent;
        }

        .roof-snow {
          position: absolute;
          top: 15px;
          left: -60px;
          width: 120px;
          height: 35px;
          background: linear-gradient(
            to bottom,
            #ffffff 0%,
            #f8f9fa 50%,
            #e8f0f8 100%
          );
          border-radius: 50% 50% 0 0;
          box-shadow: 
            0 3px 6px rgba(0, 0, 0, 0.2),
            inset 0 -2px 4px rgba(0, 0, 0, 0.1);
        }

        .cabin-walls {
          position: relative;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            135deg,
            #8b5a3c 0%,
            #6b4423 50%,
            #5a3619 100%
          );
          border: 2px solid #4a2c1a;
          box-shadow: 
            0 4px 12px rgba(0, 0, 0, 0.4),
            inset 0 0 20px rgba(0, 0, 0, 0.2);
          display: flex;
          align-items: center;
          justify-content: space-around;
          padding: 10px;
        }

        .cabin-walls-large {
          width: 130px;
        }

        .wood-texture {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          opacity: 0.3;
          background-image: 
            repeating-linear-gradient(
              90deg,
              transparent,
              transparent 3px,
              rgba(0, 0, 0, 0.1) 3px,
              rgba(0, 0, 0, 0.1) 4px
            );
        }

        .window {
          width: 32px;
          height: 40px;
          background: linear-gradient(
            135deg,
            #ffeb99 0%,
            #ffd966 50%,
            #ffb84d 100%
          );
          border: 2px solid #3d2817;
          border-radius: 2px;
          position: relative;
          box-shadow: 
            0 0 25px rgba(255, 200, 100, 0.8),
            0 0 40px rgba(255, 180, 80, 0.5),
            inset 0 0 15px rgba(255, 235, 150, 0.6);
          animation: warmWindowGlow 3s ease-in-out infinite;
          z-index: 1;
        }

        .window-right {
          animation-delay: 0.8s;
        }

        .window-second {
          animation-delay: 1.6s;
        }

        .window-frame {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 2px;
          height: 100%;
          background: rgba(40, 25, 15, 0.6);
          box-shadow: 0 0 2px rgba(0, 0, 0, 0.4);
        }

        .window-frame::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 40px;
          height: 2px;
          background: rgba(40, 25, 15, 0.6);
          box-shadow: 0 0 2px rgba(0, 0, 0, 0.4);
        }

        .window-light {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: radial-gradient(
            ellipse at 30% 30%,
            rgba(255, 255, 255, 0.4) 0%,
            transparent 60%
          );
          animation: flickerLight 4s ease-in-out infinite;
        }

        .cabin-door {
          width: 24px;
          height: 50px;
          background: linear-gradient(
            to bottom,
            #5a3619 0%,
            #3d2817 100%
          );
          border: 2px solid #2a1810;
          border-radius: 3px 3px 0 0;
          position: relative;
          box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.3);
        }

        .cabin-door::before {
          content: '';
          position: absolute;
          right: 4px;
          top: 50%;
          width: 3px;
          height: 3px;
          background: #8b7355;
          border-radius: 50%;
          box-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
        }

        .chimney {
          position: absolute;
          top: -45px;
          right: 25px;
          width: 18px;
          height: 35px;
          background: linear-gradient(
            to right,
            #8b4513 0%,
            #6b3410 50%,
            #5a2c0a 100%
          );
          border: 2px solid #4a2209;
          z-index: 0;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }

        .chimney-center {
          right: 40px;
        }

        .chimney-right {
          right: 20px;
        }

        .chimney::before {
          content: '';
          position: absolute;
          top: -4px;
          left: -2px;
          width: 22px;
          height: 6px;
          background: #8b4513;
          border-radius: 2px;
        }

        .smoke-puff {
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          width: 12px;
          height: 12px;
          background: rgba(200, 200, 200, 0.7);
          border-radius: 50%;
          animation: smokeRise 4s ease-out infinite;
          filter: blur(2px);
        }

        .foreground-trees {
          position: absolute;
          bottom: 25%;
          width: 100%;
          height: 250px;
          z-index: 5;
          opacity: 0.5;
        }

        .foreground-tree {
          position: absolute;
          bottom: 0;
          clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
          background: linear-gradient(
            to bottom,
            #1d4a1d 0%,
            #0f3a0f 50%,
            #052005 100%
          );
          opacity: 1;
        }

        .snow-ground-layer {
          position: absolute;
          bottom: 0;
          width: 100%;
          height: 25%;
          z-index: 6;
        }

        .ground-texture {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            to bottom,
            #f0f4f8 0%,
            #ffffff 30%,
            #f5f8fa 60%,
            #e8f2f8 100%
          );
          box-shadow: 
            0 -8px 25px rgba(255, 255, 255, 0.6),
            inset 0 10px 20px rgba(0, 0, 0, 0.08);
        }

        .snow-path {
          position: absolute;
          top: -5px;
          left: 35%;
          width: 30%;
          height: 100%;
          background: linear-gradient(
            to bottom,
            rgba(240, 245, 250, 0.8) 0%,
            rgba(235, 240, 245, 0.9) 100%
          );
          border-radius: 50% 50% 0 0;
          opacity: 0.6;
          filter: blur(3px);
        }

        .snow-drifts {
          position: absolute;
          top: -15px;
          left: 0;
          width: 100%;
          height: 20px;
          background: linear-gradient(
            to bottom,
            transparent 0%,
            rgba(255, 255, 255, 0.6) 100%
          );
        }

        .falling-snow-container {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 7;
          opacity: 1;
        }

        .realistic-snowflake {
          position: absolute;
          top: -10px;
          background: radial-gradient(
            circle,
            rgba(255, 255, 255, 0.9) 0%,
            rgba(255, 255, 255, 0.6) 50%,
            rgba(255, 255, 255, 0.2) 100%
          );
          border-radius: 50%;
          animation: snowfall linear infinite;
          box-shadow: 0 0 5px rgba(255, 255, 255, 0.8);
        }

        @keyframes moonHaloGlow {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }

        @keyframes moonGlow {
          0%, 100% {
            opacity: 0.8;
            transform: translate(-50%, -50%) scale(1);
          }
          50% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1.05);
          }
        }

        @keyframes starTwinkle {
          0%, 100% {
            opacity: 0.4;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.3);
          }
        }

        @keyframes treesSway {
          0%, 100% { transform: rotate(-0.5deg); }
          50% { transform: rotate(0.5deg); }
        }

        @keyframes warmWindowGlow {
          0%, 100% {
            box-shadow: 
              0 0 25px rgba(255, 200, 100, 0.8),
              0 0 40px rgba(255, 180, 80, 0.5),
              inset 0 0 15px rgba(255, 235, 150, 0.6);
          }
          50% {
            box-shadow: 
              0 0 30px rgba(255, 200, 100, 1),
              0 0 50px rgba(255, 180, 80, 0.7),
              inset 0 0 20px rgba(255, 235, 150, 0.8);
          }
        }

        @keyframes flickerLight {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.9; }
        }

        @keyframes smokeRise {
          0% {
            opacity: 0.7;
            transform: translateX(-50%) translateY(0) scale(1);
          }
          50% {
            opacity: 0.4;
            transform: translateX(calc(-50% + 10px)) translateY(-40px) scale(1.8);
          }
          100% {
            opacity: 0;
            transform: translateX(calc(-50% + 20px)) translateY(-80px) scale(2.5);
          }
        }

        @keyframes snowfall {
          0% {
            transform: translateX(0) translateY(0) rotate(0deg);
          }
          100% {
            transform: translateX(var(--sway)) translateY(100vh) rotate(360deg);
          }
        }

        @media (max-width: 768px) {
          .cabin { width: 70px; height: 60px; }
          .cabin-walls-large { width: 90px; }
          .moon-container { width: 100px; height: 100px; }
          .village-container { bottom: 18%; }
        }
      `}</style>
    </div>
  );
};

export default WinterSceneOverlay;
